import { lazy, Suspense } from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { useFlag } from '@unleash/proxy-client-react';

import { selectUser } from 'store';

import Loader from 'shared/Loader';

import { useAppSelector } from 'hooks';
import { useMyPrograms } from 'hooks/useMyPrograms/useMyPrograms';
import { useSelectFeatureStatus } from 'hooks/useSelectFeatureStatus';
import { FeatureFlag } from 'utils/enums';
import { lazyRetry } from 'utils/lazy';
import { PathName } from './paths';

import { RequireProgram, StardustPostOnboardingGuard } from './guards';

const Weight = lazy(() => lazyRetry(() => import('pages/Weight')));
const MentalHealth = lazy(() => lazyRetry(() => import('pages/MentalHealth')));
const WomensHRT = lazy(() => lazyRetry(() => import('pages/WomensHRT')));
const MensTRT = lazy(() => lazyRetry(() => import('pages/MensTRT')));
const Checkout = lazy(() => lazyRetry(() => import('pages/Checkout')));
const UpgradePlan = lazy(() => lazyRetry(() => import('pages/UpgradePlan')));
const MigrateToBundlePlan = lazy(() => lazyRetry(() => import('pages/MigrateToBundlePlan')));
const MigrateToBundlePlanFromWM = lazy(() =>
  lazyRetry(() => import('pages/MigrateToBundlePlanFromWM'))
);
const CurrentWeight = lazy(() => lazyRetry(() => import('pages/CurrentWeight')));
const PrescriptionStatus = lazy(() => lazyRetry(() => import('pages/PrescriptionStatus')));
const Survey = lazy(() => lazyRetry(() => import('pages/Survey')));
const SelectPrescriptions = lazy(() => lazyRetry(() => import('pages/SelectPrescriptions')));
const DeviceStatus = lazy(() => lazyRetry(() => import('pages/DeviceStatus')));
const AddMeasurement = lazy(() => lazyRetry(() => import('pages/AddMeasurement')));
const MigrateToPlan = lazy(() => lazyRetry(() => import('pages/MigrateToPlan')));
const PostOnboarding = lazy(() => lazyRetry(() => import('containers/PostOnboarding')));
const PostOnboardingAlt = lazy(() => lazyRetry(() => import('containers/PostOnboardingAlt')));
const PostOnboardingWH = lazy(() => lazyRetry(() => import('containers/PostOnboardingWH')));
const PostOnboardingTRT = lazy(() =>
  lazyRetry(() => import('containers/PostOnboardingWH/PostOnboardingTRT'))
);
const PostOnboardingMensTRT = lazy(() =>
  lazyRetry(() => import('containers/PostOnboardingMensTRT'))
);
const MigrateFromTirzepatide = lazy(() => lazyRetry(() => import('pages/MigrateFromTirzepatide')));
const MigrateFromSemaglutide = lazy(() => lazyRetry(() => import('pages/MigrateFromSemaglutide')));
const MigrateToTT = lazy(() => lazyRetry(() => import('containers/MigrateToTT')));
const MigrateToWM = lazy(() => lazyRetry(() => import('containers/MigrateToWM')));
const MedicareConversion = lazy(() => lazyRetry(() => import('containers/MedicareConversion')));
const ReonboardingSummary = lazy(() => lazyRetry(() => import('pages/ReonboardingSummary')));
const MedicationConfirmation = lazy(() => lazyRetry(() => import('pages/MedicationConfirmation')));

const MigrateToBundlePlanGuard = () => {
  const { weightManagement } = useMyPrograms();
  const isDisabledMigrateToBundlePlan = useSelectFeatureStatus('migrateToBundlePlan');
  const isExtendedReOnboardingWMFlowEnabled = useSelectFeatureStatus('reOnboardingWM', 'enabled');
  const isAbleToChangeToTheBundle =
    !isDisabledMigrateToBundlePlan ||
    isExtendedReOnboardingWMFlowEnabled ||
    weightManagement?.details?.requiredAppointment?.reason === 'bundled-offer';

  if (!isAbleToChangeToTheBundle) {
    return <Navigate to={PathName.NotFound} replace />;
  }

  return (
    <Suspense fallback={<Loader isVisible />}>
      {!weightManagement ? <MigrateToBundlePlan /> : <MigrateToBundlePlanFromWM />}
    </Suspense>
  );
};

const IntakeFormGuard = () => {
  const { search } = useLocation();
  const { useNewPostOnboarding } = useAppSelector(selectUser);

  if (useNewPostOnboarding) {
    return <Navigate to={{ pathname: PathName.PostOnboarding, search }} replace />;
  }

  return (
    <Suspense fallback={<Loader isVisible />}>
      <PostOnboarding />
    </Suspense>
  );
};

const MigrateToTTGuard = () => {
  const { isInsurancePatient, isTTPatient } = useMyPrograms();

  if (isTTPatient || isInsurancePatient) {
    return <Navigate to={PathName.Weight} />;
  }

  return (
    <Suspense fallback={<Loader isVisible />}>
      <MigrateToTT />
    </Suspense>
  );
};

const MigrateToWMGuard = () => {
  const { hadOrHasWeightManagement } = useMyPrograms();

  if (hadOrHasWeightManagement) {
    return <Navigate to={PathName.NotFound} replace />;
  }

  return (
    <Suspense fallback={<Loader isVisible />}>
      <MigrateToWM />
    </Suspense>
  );
};

export const MensTRTIntakeGuard = () => {
  const isDynamic = useFlag(FeatureFlag.EnablePomifDynamicTrt);
  return (
    <RequireProgram program="TRT" fallback={<Navigate to={PathName.Home} />}>
      <StardustPostOnboardingGuard category="mens-health">
        <Suspense fallback={<Loader isVisible />}>
          {isDynamic ? <PostOnboardingTRT /> : <PostOnboardingMensTRT />}
        </Suspense>
      </StardustPostOnboardingGuard>
    </RequireProgram>
  );
};

export const getMainLayoutProgramRoutes = () => (
  <>
    <Route
      element={
        <RequireProgram program="WM">
          <Weight />
        </RequireProgram>
      }
      path={PathName.Weight}
    />
    <Route
      element={
        <RequireProgram program="MH" fallback={<Navigate to={PathName.Home} />}>
          <MentalHealth />
        </RequireProgram>
      }
      path={PathName.MentalHealth}
    />
    <Route
      element={
        <RequireProgram program="WH" fallback={<Navigate to={PathName.Home} />}>
          <WomensHRT />
        </RequireProgram>
      }
      path={PathName.WomensHRT}
    />
    <Route
      element={
        <RequireProgram program="TRT" fallback={<Navigate to={PathName.Home} />}>
          <MensTRT />
        </RequireProgram>
      }
      path={PathName.MensTRT}
    />
    <Route element={<Navigate to={PathName.Weight} replace />} path="/weight-management" />
    <Route element={<MigrateToBundlePlanGuard />} path={PathName.MigrateToBundlePlan} />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <UpgradePlan />
        </Suspense>
      }
      path={PathName.UpgradePlan}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <Checkout />
        </Suspense>
      }
      path={PathName.Checkout}
    />
  </>
);

export const getProtectedProgramRoutes = () => (
  <>
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <ReonboardingSummary />
        </Suspense>
      }
      path={PathName.ReonboardingSummary}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <MedicationConfirmation />
        </Suspense>
      }
      path={PathName.MedicationConfirmation}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <CurrentWeight />
        </Suspense>
      }
      path={PathName.CurrentWeight}
    />
    <Route element={<PrescriptionStatus />} path={PathName.Prescriptions} />
    <Route element={<Survey />} path={PathName.Survey} />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <SelectPrescriptions />
        </Suspense>
      }
      path={PathName.SelectPrescriptions}
    />
    <Route
      element={
        <RequireProgram program="WM">
          <DeviceStatus />
        </RequireProgram>
      }
      path={PathName.DeviceStatus}
    />
    <Route element={<MigrateToPlan />} path={PathName.MigrateToPlan} />
    <Route
      element={
        <RequireProgram program="WM">
          <AddMeasurement />
        </RequireProgram>
      }
      path={PathName.AddMeasurement}
    />
    <Route
      element={
        <StardustPostOnboardingGuard>
          <IntakeFormGuard />
        </StardustPostOnboardingGuard>
      }
      path={PathName.IntakeForm}
    />
    <Route path={PathName.IntakeFormMensTRT} element={<MensTRTIntakeGuard />} />
    <Route
      element={
        <StardustPostOnboardingGuard>
          <Suspense fallback={<Loader isVisible />}>
            <PostOnboardingAlt />
          </Suspense>
        </StardustPostOnboardingGuard>
      }
      path={PathName.PostOnboarding}
    />
    <Route
      path={PathName.PostOnboardingWH}
      element={
        <RequireProgram program="WH" fallback={<Navigate to={PathName.Home} />}>
          <StardustPostOnboardingGuard category="women-health">
            <Suspense fallback={<Loader isVisible />}>
              <PostOnboardingWH />
            </Suspense>
          </StardustPostOnboardingGuard>
        </RequireProgram>
      }
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <MigrateFromTirzepatide />
        </Suspense>
      }
      path={PathName.MoveFromTirzepatide}
    />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <MigrateFromSemaglutide />
        </Suspense>
      }
      path={PathName.MoveFromSemaglutide}
    />
    <Route element={<MigrateToTTGuard />} path={PathName.MigrateToTT} />
    <Route element={<MigrateToWMGuard />} path={PathName.MigrateToWM} />
    <Route
      element={
        <Suspense fallback={<Loader isVisible />}>
          <MedicareConversion />
        </Suspense>
      }
      path={PathName.MedicareConversion}
    />
  </>
);
